import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  customReportsService,
  type ServiceResult,
  type CustomReportWithConfig,
  type CustomReportFormData,
  type CustomReportFolder,
} from '@/services/customReports';
import type { ReportConfig } from '@/types/custom-reports';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const customReportsKeys = {
  all: ['custom-reports'] as const,
  lists: () => [...customReportsKeys.all, 'list'] as const,
  list: (filters: { favoritesOnly?: boolean; search?: string }) =>
    [...customReportsKeys.lists(), filters] as const,
  details: () => [...customReportsKeys.all, 'detail'] as const,
  detail: (id: string) => [...customReportsKeys.details(), id] as const,
  folders: () => ['custom-report-folders'] as const,
};

// ============================================================================
// HOOKS FOR REPORTS
// ============================================================================

/**
 * Fetch all custom reports with optional filters
 */
export function useCustomReports(options?: {
  favoritesOnly?: boolean;
  search?: string;
}) {
  return useQuery({
    queryKey: customReportsKeys.list(options || {}),
    queryFn: async () => {
      const result = await customReportsService.getReports(options);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
  });
}

/**
 * Fetch a single report by ID
 */
export function useCustomReport(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customReportsKeys.detail(id),
    queryFn: async () => {
      const result = await customReportsService.getReportById(id);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
    enabled: (options?.enabled !== false) && !!id,
  });
}

/**
 * Create a new custom report
 */
export function useCreateCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CustomReportFormData) => {
      const result = await customReportsService.createReport(data);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate reports list queries
      queryClient.invalidateQueries({ queryKey: customReportsKeys.lists() });

      toast.success('Relatório criado com sucesso!', {
        description: `"${data.name}" foi adicionado aos seus relatórios.`,
      });
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar relatório', {
        description: error.message,
      });
    },
  });
}

/**
 * Update an existing report
 */
export function useUpdateCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CustomReportFormData> }) => {
      const result = await customReportsService.updateReport(id, data);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate specific report and list queries
      queryClient.invalidateQueries({ queryKey: customReportsKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: customReportsKeys.lists() });

      toast.success('Relatório atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar relatório', {
        description: error.message,
      });
    },
  });
}

/**
 * Delete a report
 */
export function useDeleteCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await customReportsService.deleteReport(id);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return id;
    },
    onSuccess: (id) => {
      // Remove specific report from cache and invalidate list
      queryClient.removeQueries({ queryKey: customReportsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: customReportsKeys.lists() });

      toast.success('Relatório excluído com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao excluir relatório', {
        description: error.message,
      });
    },
  });
}

/**
 * Duplicate a report
 */
export function useDuplicateCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await customReportsService.duplicateReport(id);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customReportsKeys.lists() });

      toast.success('Relatório duplicado com sucesso!', {
        description: `"${data.name}" foi adicionado aos seus relatórios.`,
      });
    },
    onError: (error: Error) => {
      toast.error('Erro ao duplicar relatório', {
        description: error.message,
      });
    },
  });
}

/**
 * Toggle favorite status
 */
export function useToggleFavoriteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await customReportsService.toggleFavorite(id);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customReportsKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: customReportsKeys.lists() });
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar favorito', {
        description: error.message,
      });
    },
  });
}

// ============================================================================
// HOOKS FOR FOLDERS
// ============================================================================

/**
 * Fetch all folders
 */
export function useCustomReportFolders() {
  return useQuery({
    queryKey: customReportsKeys.folders(),
    queryFn: async () => {
      const result = await customReportsService.getFolders();
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
  });
}

/**
 * Create a new folder
 */
export function useCreateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, parentId }: { name: string; parentId?: string }) => {
      const result = await customReportsService.createFolder(name, parentId);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customReportsKeys.folders() });

      toast.success('Pasta criada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar pasta', {
        description: error.message,
      });
    },
  });
}

/**
 * Delete a folder
 */
export function useDeleteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await customReportsService.deleteFolder(id);
      if (result.error) {
        throw new Error(String(result.error));
      }
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customReportsKeys.folders() });

      toast.success('Pasta excluída com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao excluir pasta', {
        description: error.message,
      });
    },
  });
}
